import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

@Component({
  selector: 'app-field-editor',
  templateUrl: './field-editor.component.html',
  styleUrls: ['./field-editor.component.scss']
})
export class FieldEditorComponent implements OnInit {

  @Input() field: any;
  @Input() mode: string; // 'report' | 'view'
  @Input() index: number;
  @Input() count: number;

  @Output() changed = new EventEmitter<void>();
  @Output() remove = new EventEmitter<void>();
  @Output() moveUp = new EventEmitter<void>();
  @Output() moveDown = new EventEmitter<void>();

  reportFieldTypes = [
    'string', 'number', 'numberInverse', 'currency', 'currencyDetail', 'currencyInverse',
    'percentage', 'percent', 'date', 'dateTime', 'dateTimeYMD', 'dateTimeGeneric',
    'boolean', 'id', 'remoteId', 'postQueryLookup', 'sourceCollection',
    'barcode', 'email', 'phone', 'cardNumber'
  ];

  viewFieldTypes = [
    'string', 'number', 'integer', 'currency', 'advancedPrice', 'percent', 'dateTime', 'boolean',
    'telephone', 'address', 'image', 'richText', 'sourceCollection', 'detailLink', 'notes'
  ];

  expanded = false;

  aliasText = '';
  displayPathText = '';
  optionsText = '';
  trueValueText = '';
  falseValueText = '';

  ngOnInit(): void {
    this.aliasText = this.listToText(this.field.alias);
    this.optionsText = this.listToText(this.field.options);
    this.trueValueText = this.field.trueValue != null ? String(this.field.trueValue) : '';
    this.falseValueText = this.field.falseValue != null ? String(this.field.falseValue) : '';

    if (this.field.source) {
      this.displayPathText = this.listToText(this.field.source.displayPath);
    }

    if (!this.field.path && !this.field.name) {
      this.expanded = true;
    }
  }

  get fieldTypes() {
    return this.mode === 'report' ? this.reportFieldTypes : this.viewFieldTypes;
  }

  get showSource() {
    return this.field.type === 'sourceCollection';
  }

  get showDetailLink() {
    return this.mode === 'view' && this.field.type === 'detailLink';
  }

  get showNotes() {
    return this.mode === 'view' && this.field.type === 'notes';
  }

  get showRemoteJoin() {
    return this.mode === 'report' && (this.field.type === 'remoteId' || this.field.type === 'postQueryLookup');
  }

  listToText(value): string {
    if (!value) {
      return '';
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return String(value);
  }

  textToList(text: string) {
    const items = text.split(',').map(item => item.trim()).filter(item => item !== '');
    if (items.length === 0) {
      return null;
    }
    if (items.length === 1) {
      return items[0];
    }
    return items;
  }

  emitChanged() {
    this.changed.emit();
  }

  typeChanged() {
    if (this.showSource && !this.field.source) {
      this.field.source = {
        collection: '',
        path: '_id',
        displayPath: 'name'
      };
      this.displayPathText = 'name';
    }
    this.emitChanged();
  }

  setFlag(key: string, checked: boolean) {
    if (checked) {
      this.field[key] = true;
    } else {
      delete this.field[key];
    }
    this.emitChanged();
  }

  setNumber(key: string, value) {
    const parsed = parseFloat(value);
    if (isNaN(parsed)) {
      delete this.field[key];
    } else {
      this.field[key] = parsed;
    }
    this.emitChanged();
  }

  setText(target: any, key: string, value: string) {
    if (value === '' || value === null || value === undefined) {
      delete target[key];
    } else {
      target[key] = value;
    }
    this.emitChanged();
  }

  aliasChanged() {
    const alias = this.textToList(this.aliasText);
    if (alias === null) {
      delete this.field.alias;
    } else {
      this.field.alias = alias;
    }
    this.emitChanged();
  }

  // Options are always stored as an array of strings (unlike alias, which
  // collapses a single item to a plain string).
  // Boolean mapping (view fields): blank ON value = plain true/false field.
  booleanValuesChanged() {
    const onValue = this.trueValueText.trim();
    const offValue = this.falseValueText.trim();
    if (onValue === '') {
      delete this.field.trueValue;
      delete this.field.falseValue;
    } else {
      this.field.trueValue = onValue;
      this.field.falseValue = offValue === '' ? null : offValue;
    }
    this.emitChanged();
  }

  optionsChanged() {
    const items = this.optionsText.split(',').map(item => item.trim()).filter(item => item !== '');
    if (items.length === 0) {
      delete this.field.options;
    } else {
      this.field.options = items;
    }
    this.emitChanged();
  }

  displayPathChanged() {
    if (!this.field.source) {
      return;
    }
    const displayPath = this.textToList(this.displayPathText);
    if (displayPath === null) {
      delete this.field.source.displayPath;
    } else {
      this.field.source.displayPath = displayPath;
    }
    this.emitChanged();
  }

  sourceFiltersChanged(filters) {
    if (!this.field.source) {
      return;
    }
    if (filters && Object.keys(filters).length > 0) {
      this.field.source.filters = filters;
    } else {
      delete this.field.source.filters;
    }
    this.emitChanged();
  }

  toggleRemoteLookup(checked: boolean) {
    if (checked) {
      this.field.remoteLookup = this.field.remoteLookup || {
        source: 'fireTowerApi',
        lookupKeyLocal: '',
        lookupKeyRemote: '',
        remoteFieldKey: ''
      };
    } else {
      delete this.field.remoteLookup;
    }
    this.emitChanged();
  }

  toggleCalculated(checked: boolean) {
    if (checked) {
      this.field.calculated = true;
      if (!this.field.formula) {
        this.field.formula = '';
      }
    } else {
      delete this.field.calculated;
      delete this.field.formula;
    }
    this.emitChanged();
  }

  addCacheField() {
    if (!this.field.cacheFields) {
      this.field.cacheFields = [];
    }
    this.field.cacheFields.push({ sourcePath: '', cachePath: '' });
    this.emitChanged();
  }

  removeCacheField(index: number) {
    this.field.cacheFields.splice(index, 1);
    if (this.field.cacheFields.length === 0) {
      delete this.field.cacheFields;
    }
    this.emitChanged();
  }
}
